"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, Database, Scale, ChartBar as BarChart2 } from 'lucide-react';
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const metrics = [{
  id: 1,
  figure: "100%",
  label: "Primary source verification",
  description: "Every dataset is gathered directly from verified corporate practitioners.",
  icon: Database
}, {
  id: 2,
  figure: "2,400+",
  label: "Senior leaders surveyed",
  description: "Annual executive cohorts spanning enterprise technology, finance, and operations.",
  icon: BarChart2
}, {
  id: 3,
  figure: "0%",
  label: "Sponsored bias",
  description: "Complete editorial independence with zero vendor-funded conclusions.",
  icon: ShieldCheck
}, {
  id: 4,
  figure: "14",
  label: "Methodology checkpoints",
  description: "Multi-tiered peer review and statistical validation prior to release.",
  icon: Scale
}];
export default function Intro() {
  const shouldReduceMotion = useReducedMotion();
  const fadeInVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 12
    },
    visible: (custom: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: shouldReduceMotion ? 0 : 0.5,
        delay: shouldReduceMotion ? 0 : custom * 0.08,
        ease: [0.16, 1, 0.3, 1] as const
      }
    })
  };
  return <section id="intro" data-nav="light" className="relative bg-background py-16 sm:py-20 lg:py-24 border-b border-border">
      <div className="mx-auto max-w-[1280px] px-5 sm:px-8 lg:px-12">
        {/* Masthead Rule Motif */}
        <div className="mb-10 sm:mb-14">
          <div className="h-px w-full bg-foreground" />
          <div className="mt-1 h-px w-full bg-border" />
        </div>

        {/* Top Header & Editorial Statement Split */}
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12 items-start">
          {/* Eyebrow & Main Title */}
          <motion.div custom={0} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} variants={fadeInVariants} className="lg:col-span-5">
            <span className="text-xs font-semibold tracking-wider text-muted-foreground uppercase">
              Research &amp; Methodologies
            </span>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl font-semibold tracking-tight text-foreground leading-tight">
              Rigorous empirical data for boardroom decisions.
            </h2>
            <div className="mt-6 flex items-center gap-3">
              <div className="h-0.5 w-10 bg-accent" />
              <span className="text-xs font-mono text-muted-foreground uppercase tracking-widest">
                Editorial Standard
              </span>
            </div>
          </motion.div>

          {/* Two-Paragraph Editorial Statement */}
          <motion.div custom={1} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} variants={fadeInVariants} className="lg:col-span-7 flex flex-col gap-5 text-base sm:text-lg text-muted-foreground leading-relaxed font-sans">
            <p>
              MinfoMedia research reports examine enterprise transformation through direct practitioner surveys, longitudinal benchmarks, and reproducible empirical evidence. Rather than republishing vendor surveys or unverified estimates, our editorial analysts build proprietary datasets across commercial sectors.
            </p>
            <p>
              Each publication undergoes a multi-stage review process before release. We document our sampling frames, statistical confidence levels, and analytical constraints openly, ensuring business leaders evaluate industry developments with uncompromised clarity and verifiable accuracy.
            </p>
          </motion.div>
        </div>

        {/* Separator */}
        <Separator className="my-12 sm:my-16" />

        {/* Research Integrity Metrics Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {metrics.map((item, i) => {
          const Icon = item.icon;
          return <motion.div key={item.id} data-index={i} custom={i + 2} initial="hidden" whileInView="visible" viewport={{
            once: true,
            amount: 0.15
          }} variants={fadeInVariants}>
                <Card className="h-full border border-border bg-card rounded-md shadow-xs transition-[border-color,box-shadow] duration-200 hover:border-foreground/30 hover:shadow-sm">
                  <CardContent className="p-6 flex flex-col justify-between h-full">
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex h-9 w-9 items-center justify-center rounded-sm bg-secondary text-secondary-foreground">
                          <Icon className="h-4 w-4 text-foreground" />
                        </div>
                        <span className="text-[11px] font-mono text-muted-foreground tracking-normal uppercase">
                          Standard #{item.id}
                        </span>
                      </div>
                      <div className="font-serif text-3xl font-semibold text-foreground tracking-tight">
                        {item.figure}
                      </div>
                      <div className="mt-1 text-sm font-semibold text-foreground">
                        {item.label}
                      </div>
                    </div>
                    <p className="mt-4 text-xs text-muted-foreground leading-normal border-t border-border pt-4">
                      {item.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </div>
      </div>
    </section>;
}